import React, { Component } from "react";
import PageContext, { getPageContext, updatePageContext } from "../../components/Common/PageContext";
import { withStyles } from "@mui/styles";
import {
	postSubmitReceiveAsync,
	fetchReceivePurchaseOrderDetails,
	upsertCreditRequest,
	getCreditRequest,
} from "../../services/apiHelper";
import qs from "qs";
import { setStateAsync } from "../../util/stateHelper";
import CreditRequestDialog from "../../components/CreditRequest/CreditRequestDialog";
import { LoadingDialog } from "../../components/Loading/LoadingDialog";
import { creditReason, creditStatus } from "../../util/creditRequestMapping";
import { v4 as uuidv4 } from "uuid";
import { receiveStatus } from "../../util/receiveStatus";
import { getLineItemStatusByQuantity, isLineItemAutoApproved } from "../../util/creditRequestUtil";
import SnackbarAlert from "../../components/Snackbar/SnackbarAlert";
import { isFullyReceived } from "../../util/receiveUtil";
import ModalReceiveFinalise from "../../components/Modal/ModalReceiveFinalise";
import { loadingDialogClosed, modalClosed } from "../../util/emptyObjectsUtil";
import { isFeatureEnabledForLocationAndReceiveType } from "../../util/featureUtil";
import { Modal } from "../../components/Modal/Modal";
import { modals } from "../../util/modalUtil";

const styles = (theme: any) => ({
	speedDial: {
		position: "fixed" as const,
		bottom: theme.spacing(2),
		right: theme.spacing(2),
	},
	backDrop: {
		zIndex: 1000,
	},
});

const newContext = {
	isNew: true,
	heading: "Create Credit Request",
	message:
		"There are some items not fully received for this invoice. Please select a reason for the outstanding items below.",
	buttonName: "Create credit request",
	finaliseReceive: false,
};

const updateContext = {
	isNew: false,
	heading: "Update Credit Request",
	message: "Receive quantities have changed and therefore linked credit request must be updated. Please review below.",
	buttonName: "Update credit request",
	finaliseReceive: false,
};

const cancelContext = {
	isNew: false,
	heading: "Finalise Receive",
	message:
		"This invoice will now be fully received, and the linked credit requests will be deleted. Please review below.",
	buttonName: "Finalise receive",
	finaliseReceive: true,
};

interface CreditRequestProps {
	classes?: any;
	graphProfile?: any;
	location?: any;
	history?: any;
}

interface CreditRequestState {
	receive: any;
	invoice: any;
	creditRequest: any;
	isSaveAndContinueDisabled: boolean;
	creditRequestReasons: any[];
	loadingDialog: { isOpen: boolean; message: string };
	isInErrorState: boolean;
	context: typeof newContext | typeof updateContext | typeof cancelContext;
	snackbarAlert: { isOpen: boolean; message: string; severity: string };
	isFinaliseModalOpen: boolean;
	enableProductLabelAutoPrint: boolean;
	modal: any;
	receiveDetails?: any;
	hasLabels?: boolean;
	rootCourier: string;
}

class CreditRequest extends Component<CreditRequestProps, CreditRequestState> {
	static contextType = PageContext;
	setStateAsync: (state: Partial<CreditRequestState>) => Promise<void>;
	updatePageContext: (context: any, callback?: () => void) => void;
	getPageContext: () => any;

	constructor(props: CreditRequestProps) {
		super(props);
		this.state = {
			receive: null,
			invoice: null,
			creditRequest: null,
			isSaveAndContinueDisabled: true,
			creditRequestReasons: [],
			loadingDialog: {
				isOpen: true,
				message: "Loading... ",
			},
			isInErrorState: false,
			context: newContext,
			snackbarAlert: {
				isOpen: false,
				message: "",
				severity: "",
			},
			isFinaliseModalOpen: false,
			enableProductLabelAutoPrint: false,
			modal: modalClosed,
			rootCourier: "",
		};
		this.setStateAsync = setStateAsync.bind(this);
		this.updatePageContext = updatePageContext.bind(this);
		this.getPageContext = getPageContext.bind(this);
	}

	async componentDidMount() {
		try {
			let {
				creditRequestReasons,
				featureConfiguration: { productLabelAutoPrint },
			} = this.getPageContext();

			const queryStringObject = qs.parse(this.props.location.search, { ignoreQueryPrefix: true });
			const { id, purchaseOrderNumber, invoiceNumber } = queryStringObject;

			let receiveDetails = null;

			// If viewing an existing credit request by ID, fetch it directly
			if (id) {
				const creditRequestResponse = await getCreditRequest(id) as any;
				if (!creditRequestResponse) {
					this.setStateAsync({
						modal: {
							...modals.genericError,
							title: "Credit Request Processing",
							paragraphs: ["The credit request is currently processing and will be ready soon; please try again shortly."],
							primaryButtonName: "Back",
							onPrimaryButtonClick: this.cancelCreateCreditRequest,
						},
						loadingDialog: loadingDialogClosed,
						isInErrorState: true,
					});
					return;
				}

				// Fetch the full receive details using the credit request's PO and invoice
				const receiveDetailsResponse = await fetchReceivePurchaseOrderDetails(
					creditRequestResponse.purchaseOrderNumber,
					creditRequestResponse.invoiceNumber,
					null
				) as any;

				// Validate response data exists
				if (!receiveDetailsResponse?.data?.invoice || !receiveDetailsResponse?.data?.matchedReceive || !receiveDetailsResponse?.data?.purchaseOrder) {
					this.setStateAsync({
						modal: {
							...modals.genericError,
							title: "Error Loading Credit Request",
							paragraphs: ["Unable to load the receive details for this credit request. The data may be incomplete or unavailable."],
							primaryButtonName: "Back",
							onPrimaryButtonClick: this.cancelCreateCreditRequest,
						},
						loadingDialog: loadingDialogClosed,
						isInErrorState: true,
					});
					return;
				}

				receiveDetails = {
					Invoice: receiveDetailsResponse.data.invoice,
					MatchedReceive: receiveDetailsResponse.data.matchedReceive,
					PurchaseOrder: receiveDetailsResponse.data.purchaseOrder,
					CreditRequest: creditRequestResponse,
				};
			}
			// If creating/updating using purchaseOrderNumber and invoiceNumber
			else if (purchaseOrderNumber && invoiceNumber) {
				const receiveDetailsResponse = await fetchReceivePurchaseOrderDetails(purchaseOrderNumber, invoiceNumber, null) as any;

				// Validate response data exists
				if (!receiveDetailsResponse?.data?.invoice || !receiveDetailsResponse?.data?.matchedReceive || !receiveDetailsResponse?.data?.purchaseOrder) {
					this.setStateAsync({
						modal: {
							...modals.genericError,
							title: "Error Loading Credit Request",
							paragraphs: ["Unable to load the receive details. The data may be incomplete or unavailable."],
							primaryButtonName: "Back",
							onPrimaryButtonClick: this.cancelCreateCreditRequest,
						},
						loadingDialog: loadingDialogClosed,
						isInErrorState: true,
					});
					return;
				}

				receiveDetails = {
					Invoice: receiveDetailsResponse.data.invoice,
					MatchedReceive: receiveDetailsResponse.data.matchedReceive,
					PurchaseOrder: receiveDetailsResponse.data.purchaseOrder,
					CreditRequest: receiveDetailsResponse.data.creditRequest,
				};
			}
			// Missing required parameters
			else {
				this.setStateAsync({
					modal: {
						...modals.genericError,
						title: "Missing Information",
						paragraphs: ["Unable to load credit request. Missing required purchase order or invoice details."],
						primaryButtonName: "Back",
						onPrimaryButtonClick: this.returnToHome,
					},
					loadingDialog: loadingDialogClosed,
					isInErrorState: true,
				});
				return;
			}

			this.updatePageContext({
				receiveDetails,
			});

			// If page context already contains credit requests then use first instance, otherwise create new.
			let creditRequest;
			if (receiveDetails.CreditRequest) {
				await this.setStateAsync({
					context: isFullyReceived(receiveDetails.MatchedReceive) ? cancelContext : updateContext,
				});
				creditRequest = this.updateCreditRequest();
			} else {
				creditRequest = this.generateInitialCreditRequest();
			}

			await this.setStateAsync({
				receiveDetails: {
					...receiveDetails,
					CreditRequest: creditRequest,
				},
				creditRequestReasons: creditRequestReasons,
				loadingDialog: loadingDialogClosed,
				hasLabels: receiveDetails.MatchedReceive.receiveLineItems.some((li) => li.hasLabel),
				enableProductLabelAutoPrint: isFeatureEnabledForLocationAndReceiveType(
					receiveDetails?.PurchaseOrder?.shipToLocation?.locationId ?? 0,
					productLabelAutoPrint,
					receiveDetails?.MatchedReceive?.receiveType
				),
				rootCourier: creditRequest.lineItems[0]?.receivedCarrierName ?? "",
			});
		} catch (e) {
			console.error("Error loading credit request:", e);
			this.setStateAsync({
				modal: {
					...modals.genericError,
					title: "Error Loading Credit Request",
					paragraphs: [e?.message || "An unexpected error occurred while loading the credit request. Please try again or contact IT support if the issue persists."],
					primaryButtonName: "Back",
					onPrimaryButtonClick: this.cancelCreateCreditRequest,
				},
				isInErrorState: true,
				loadingDialog: loadingDialogClosed,
			});
		}
	}

	updateLineItemAsync = async (lineItem) => {
		const { receiveDetails } = this.state;
		const { CreditRequest } = receiveDetails;
		const lineItemIndex = CreditRequest.lineItems.findIndex((li) => li.id === lineItem.id);
		const updatedReceiveDetails = {
			...receiveDetails,
			CreditRequest: {
				...CreditRequest,
				lineItems: [
					...CreditRequest.lineItems.slice(0, lineItemIndex),
					{
						...lineItem,
						modifiedByUserName: this.props.graphProfile.displayName,
						modifiedByUserId: this.props.graphProfile.staffId,
					},
					...CreditRequest.lineItems.slice(lineItemIndex + 1),
				],
				modifiedByUserName: this.props.graphProfile.displayName,
				modifiedByUserId: this.props.graphProfile.staffId,
			},
		};
		await this.setStateAsync({
			receiveDetails: updatedReceiveDetails,
		});
	};

	copyLineItemDetailsToAllLinesAsync = async (lineItem) => {
		const { receiveDetails } = this.state;
		const updatedReceiveDetails = {
			...receiveDetails,
			CreditRequest: {
				...receiveDetails.CreditRequest,
				lineItems: receiveDetails.CreditRequest.lineItems.map((li) => ({
					...li,
					reason: lineItem.reason,
					receivedConsignmentNumber: lineItem.receivedConsignmentNumber,
				})),
			},
		};
		await this.setStateAsync({ receiveDetails: updatedReceiveDetails });
	};

	onCourierChangeAsync = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const courier: string = event.target.value;
		const { receiveDetails } = this.state;
		const updatedReceiveDetails = {
			...receiveDetails,
			CreditRequest: {
				...receiveDetails.CreditRequest,
				lineItems: receiveDetails.CreditRequest.lineItems.map((li) => ({
					...li,
					receivedCarrierName: courier,
				})),
			},
		};
		await this.setStateAsync({ rootCourier: courier, receiveDetails: updatedReceiveDetails });
	};

	generateInitialCreditRequest = () => {
		const { receiveDetails } = this.getPageContext();
		const { MatchedReceive, PurchaseOrder } = receiveDetails;
		const { staffId, displayName } = this.props.graphProfile;
		const currentDate = new Date();
		let creditRequestLineItems = MatchedReceive.receiveLineItems
			.filter((item) => item.receivedQuantity < item.invoicedQuantity)
			.map((item) => {
				return this.mapCreditRequestLineItemFromReceiveLineItem(item);
			});

		return {
			id: uuidv4(),
			receiveId: MatchedReceive.id,
			supplierId: MatchedReceive.supplierId,
			supplierName: MatchedReceive.supplierName,
			locationId: MatchedReceive.locationId,
			locationName: PurchaseOrder.shipToLocation.companyName,
			purchaseOrderNumber: MatchedReceive.purchaseOrderNumber,
			invoiceNumber: MatchedReceive.invoiceNumber,
			dateCreated: currentDate,
			createdByUserId: staffId,
			createdByUserName: displayName,
			dateModified: currentDate,
			modifiedByUserId: staffId,
			modifiedByUserName: displayName,
			totalCostEx: creditRequestLineItems.reduce((total, current) => total + current.lineCostEx, 0),
			totalCostInc: creditRequestLineItems.reduce((total, current) => total + current.lineCostInc, 0),
			lineItems: creditRequestLineItems,
			status: creditStatus.inProgress,
		};
	};

	mapCreditRequestLineItemFromReceiveLineItem = (receiveLineItem) => {
		const { staffId, displayName } = this.props.graphProfile;
		const currentDate = new Date();
		const quantity = receiveLineItem.invoicedQuantity - receiveLineItem.receivedQuantity;
		return {
			id: uuidv4(),
			sku: receiveLineItem.sku,
			modelNumber: receiveLineItem.model,
			description: receiveLineItem.description,
			quantity,
			unitCostEx: receiveLineItem.unitCostEx,
			unitCostInc: receiveLineItem.unitCostInc,
			lineCostEx: receiveLineItem.unitCostEx * quantity,
			lineCostInc: receiveLineItem.unitCostInc * quantity,
			reason: null,
			status: getLineItemStatusByQuantity(quantity),
			isAutoApprove: false,
			dateCreated: currentDate,
			createdByUserId: staffId,
			createdByUserName: displayName,
			dateModified: currentDate,
			modifiedByUserId: staffId,
			modifiedByUserName: displayName,
		};
	};

	updateCreditRequest = () => {
		let { receiveDetails } = this.getPageContext();
		let { MatchedReceive, CreditRequest } = receiveDetails;
		const { staffId, displayName } = this.props.graphProfile;

		const shortReceivedLineItems = MatchedReceive.receiveLineItems.filter(
			(ri) => ri.receivedQuantity < ri.invoicedQuantity
		);

		for (let i = 0; i < shortReceivedLineItems.length; i++) {
			const receiveLineItem = shortReceivedLineItems[i];
			const creditRequestLineItemExists = CreditRequest.lineItems.some((li) => li.sku === receiveLineItem.sku);
			if (!creditRequestLineItemExists) {
				CreditRequest.lineItems.push(this.mapCreditRequestLineItemFromReceiveLineItem(receiveLineItem));
			}
		}

		return {
			...CreditRequest,
			modifiedByUserId: staffId,
			modifiedByUserName: displayName,
			lineItems: CreditRequest.lineItems.map((li) => {
				const receiveLineItem = MatchedReceive.receiveLineItems.find((ri) => ri.sku === li.sku);
				const creditedQuantity = receiveLineItem.invoicedQuantity - receiveLineItem.receivedQuantity;
				return {
					...li,
					originalQuantity: li.quantity,
					quantity: creditedQuantity,
					lineCostEx: receiveLineItem.unitCostEx * creditedQuantity,
					lineCostInc: receiveLineItem.unitCostInc * creditedQuantity,
					status: getLineItemStatusByQuantity(creditedQuantity),
				};
			}),
		};
	};

	cancelCreateCreditRequest = () => {
		this.props.history.goBack();
	};

	returnToHome = () => {
		this.props.history.push("/");
	};

	onCloseModal = () => {
		this.setStateAsync({ modal: modalClosed });
	};

	onSaveAndContinueClickAsync = async () => {
		await this.setStateAsync({ isFinaliseModalOpen: true });
	};

	onFinaliseSubmit = async (customerOrders, hasLabels, skipAutoOrderAllocation, skipProductLabelPrinting) => {
		await this.setStateAsync({
			loadingDialog: { isOpen: true, message: "Submitting ..." },
		});
		try {
			const { receiveDetails, context } = this.state;
			const { selectedPrinterId, selectedLabelPrinterId, userLocation } = this.getPageContext();
			const { CreditRequest, MatchedReceive } = receiveDetails;

			const receiveToUpdate = { ...MatchedReceive };
			if (context.finaliseReceive) {
				receiveToUpdate.status = receiveStatus.pendingFinalise;
			} else {
				receiveToUpdate.status = receiveStatus.pendingReceive;
			}

			await postSubmitReceiveAsync(
				receiveToUpdate,
				customerOrders,
				selectedPrinterId,
				selectedLabelPrinterId,
				userLocation,
				context.finaliseReceive,
				hasLabels,
				skipAutoOrderAllocation,
				skipProductLabelPrinting
			);

			const updatedCreditRequest = {
				...CreditRequest,
				lineItems: CreditRequest.lineItems.map((li) => {
					const isAutoApprove = isLineItemAutoApproved(li, CreditRequest);
					return {
						...li,
						reference:
							li.reason === creditReason.shortSupply || li.reason === creditReason.damagedAndRejected
								? CreditRequest.invoiceNumber
								: null,
						isAutoApprove,
					};
				}),
			};
			const creditRequestResponse = await upsertCreditRequest(updatedCreditRequest) as any;
			this.updatePageContext(
				{
					receiveDetails: {
						...receiveDetails,
						CreditRequest: creditRequestResponse.data,
					},
					creditRequestReasons: this.state.creditRequestReasons,
				},
				() => {
					this.props.history.goBack();
				}
			);
		} catch (e) {
			console.log(e);
			await this.setStateAsync({
				snackbarAlert: {
					isOpen: true,
					message: "Something went wrong. Please try again later or contact IT support if issue persists.",
					severity: "error",
				},
			});
		}
		await this.setStateAsync({ loadingDialog: loadingDialogClosed });
	};

	onCancelFinalise = async () => {
		await this.setStateAsync({ isFinaliseModalOpen: false });
	};

	handleCloseSnackbarAlertAsync = async () => {
		this.setStateAsync({ snackbarAlert: { ...this.state.snackbarAlert, isOpen: false } });
	};

	isContinueButtonDisabled = () => {
		return (
			this.state.receiveDetails?.CreditRequest?.lineItems?.some(
				(item) => !item.reason || !item.receivedConsignmentNumber || !item.receivedCarrierName
			) || this.state.isInErrorState
		);
	};

	render() {
		const { loadingDialog } = this.state;
		return (
			<div>
				<CreditRequestDialog
					isOpen={!loadingDialog.isOpen}
					cancelButtonName="Cancel and return to receive"
					continueButtonName={this.state.context.buttonName}
					onCancelButtonClick={this.cancelCreateCreditRequest}
					onContinueButtonClick={this.onSaveAndContinueClickAsync}
					isContinueButtonDisabled={this.isContinueButtonDisabled()}
					creditRequestLineItems={this.state.receiveDetails?.CreditRequest?.lineItems ?? []}
					creditRequestReasons={this.state.creditRequestReasons}
					isInErrorState={this.state.isInErrorState}
					titleMessage={this.state.context.message}
					headingText={this.state.context.heading}
					updateLineItem={this.updateLineItemAsync}
					copyLineItemDetailsToAllLines={this.copyLineItemDetailsToAllLinesAsync}
					courier={this.state.rootCourier}
					onCourierChange={this.onCourierChangeAsync}
					showCopyToAllButton={
						this.state.context.isNew && this.state.receiveDetails?.CreditRequest?.lineItems.length > 1
					}
				/>
				{this.state.isFinaliseModalOpen && (
					<ModalReceiveFinalise
						isOpen={this.state.isFinaliseModalOpen}
						hasExistingCredit={!this.state.context.finaliseReceive}
						skipAllocation={false}
						onFinaliseSubmit={this.onFinaliseSubmit}
						onCancelFinalise={this.onCancelFinalise}
						locationId={this.state.receiveDetails?.PurchaseOrder.shipToLocation.locationId}
						receive={this.state.receiveDetails?.MatchedReceive}
						hasLabels={this.state.enableProductLabelAutoPrint && this.state.hasLabels}
					/>
				)}
				<LoadingDialog message={loadingDialog.message} isOpen={loadingDialog.isOpen} />
				<SnackbarAlert
					open={this.state.snackbarAlert.isOpen}
					message={this.state.snackbarAlert.message}
					severity={this.state.snackbarAlert.severity}
					onClose={this.handleCloseSnackbarAlertAsync}
				/>
				<Modal {...this.state.modal} />
			</div>
		);
	}
}

export default withStyles(styles)(CreditRequest);
